import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  StyledModal,
  GenericCameraSourceSettings,
  AgoraCameraPreview
} from '@shoflo/polymer';
import {
  setAudioInputDevice,
  setAudioOutputDevice,
  setTransformedVideoInputs,
  setVideoInputDevice,
  uploadUserVirtualBackground,
  deleteUserVirtualBackground
} from '@shoflo/studio-redux/actions';

import {
  knock as knockAction,
  notifyDirectorAttendeeLeaving
} from 'actions/Broadcast';
import { clearModal } from 'actions';
import useIsMobile from 'hooks/useIsMobile';
import { GENERIC_UPDATE_SETTINGS_MODAL } from 'constants/ModalTypes';
import { useGlobalUnload } from 'hooks/useWindowEvent';
import {
  setUserBackground,
  addToAttendeeTiles,
  showNotification
} from '@shoflo/studio-redux/actions';
import {
  PASSED,
  VENDOR_AGORA,
  RESET_USER_VIRTUAL_BACKGROUND
} from '@studio/constants';
import { updateSource } from 'actions/source';
import { shouldJoinWithoutKnock } from 'utils/broadcastPresenterHelper';
import { localAttendeeSource } from 'selectors/localAttendeeSource';
import setGenericAudioInputDevice from '@shoflo/studio-redux/actions/meeting/setGenericAudioInputDevice';
import {
  clearVirtualBackground as agoraClearVirtualBackground,
  getVideoInput as agoraGetVideoInput,
  initializeVirtualBackground as agoraInitializeVirtualBackground,
  clearPreviewVirtualBackground
} from '@cvent/studio-agora/virtualBackgrounds/virtualBackground';
import { switchVideoInput as agoraSwitchVideoInput } from '@cvent/studio-agora/video/videoHelpers';
import { switchAudioInput as agoraSwitchAudioInput } from '@cvent/studio-agora/audio/audioHelper';

const VENDOR_CONFIG_MAP = {
  [VENDOR_AGORA]: {
    cameraPreviewElement: AgoraCameraPreview,
    clearVirtualBackground: agoraClearVirtualBackground,
    initializeBackgroundFilter: agoraInitializeVirtualBackground,
    getVideoInput: agoraGetVideoInput,
    switchVideoInput: agoraSwitchVideoInput,
    switchAudioInput: agoraSwitchAudioInput
  }
};

const SettingsModal = () => {
  const attendeeId = useSelector(state => state.meeting.attendeeId);
  const attendeeTiles = useSelector(state => state.meeting.attendeeTiles);
  const client = useSelector(state => state.meeting.client, shallowEqual);
  const modal = useSelector(state => state.modal);
  const knock = useSelector(state => state.knock);
  const user = useSelector(state => state.user, shallowEqual);
  const audioInputDevice = useSelector(state => state.audioInputDevice);
  const audioOutputDevice = useSelector(state => state.audioOutputDevice);
  const videoInputDevice = useSelector(state => state.videoInputDevice);
  const userBackground = useSelector(state => state.userBackground);
  const transformedVideoInputs = useSelector(
    state => state.transformedVideoInputs
  );
  const cameraStatus = useSelector(state => state.readinessCheck.camera);
  const localSource = useSelector(localAttendeeSource, shallowEqual);

  const vendor = modal?.initialValue?.vendor;
  const vendorConfig = VENDOR_CONFIG_MAP[vendor] ?? {};

  const joinWithoutKnockEnabled = shouldJoinWithoutKnock();

  const isCameraAvailable = cameraStatus === PASSED;

  const isMobile = useIsMobile();

  let isOpen = false;

  if (modal) {
    isOpen = modal.type === GENERIC_UPDATE_SETTINGS_MODAL;
  }

  const dispatch = useDispatch();

  const currentVideoTrack = attendeeTiles[attendeeId];

  const [presenterTitle, setPresenterTitle] = useState('');
  const [presenterDescription, setPresenterDescription] = useState('');

  const handlePageReloadOrClose = () => {
    dispatch(notifyDirectorAttendeeLeaving({ closedOrReloadTab: true }, true));
  };

  const onCancel = async initialDataPayload => {
    const { transformedVideoInputs, videoInput, videoTrack } =
      initialDataPayload;

    if (videoTrack) {
      dispatch(addToAttendeeTiles(attendeeId, videoTrack));
    }
    dispatch(setVideoInputDevice(videoInput));
    dispatch(setTransformedVideoInputs(transformedVideoInputs));

    dispatch(clearModal(modal.type));
  };

  useGlobalUnload(handlePageReloadOrClose);
  useEffect(() => {
    if (knock) {
      setPresenterTitle(knock?.name);
      setPresenterDescription(knock?.description);
    }
  }, [knock]);

  useEffect(() => {
    if (localSource && joinWithoutKnockEnabled) {
      setPresenterTitle(localSource.title);
      setPresenterDescription(localSource.description);
    }
  }, [localSource, joinWithoutKnockEnabled]);

  const onSubmit = data => {
    const {
      audioInputDeviceId,
      audioOutputDeviceId,
      title,
      description,
      userBackground,
      transformedVideoInputs,
      videoInput,
      videoTrack
    } = data;

    if (videoTrack) {
      dispatch(addToAttendeeTiles(attendeeId, videoTrack));
    }
    dispatch(setGenericAudioInputDevice(audioInputDeviceId));
    dispatch(setAudioInputDevice(audioInputDeviceId));
    dispatch(setVideoInputDevice(videoInput));
    dispatch(setAudioOutputDevice(audioOutputDeviceId));
    dispatch(setUserBackground(userBackground));
    dispatch(setTransformedVideoInputs(transformedVideoInputs));

    if (localSource && joinWithoutKnockEnabled) {
      dispatch(updateSource(localSource._id, { title, description }));
    }

    if (!joinWithoutKnockEnabled) {
      dispatch(
        knockAction({
          presenterName: title,
          isTransient: false,
          presenterDescription: description || '',
          presenterDetailsUpdated: true,
          presenterPhoto: user?.photo
        })
      );
    }
    dispatch(clearModal(modal.type));
  };

  const style = { maxWidth: '800px' };

  const handleDeleteUserVirtualBackground = () => {
    dispatch(deleteUserVirtualBackground());
  };
  const handleUploadUserVirtualBackground = file =>
    dispatch(uploadUserVirtualBackground(file));
  const handleResetUserVirtualBackground = () =>
    dispatch({ type: RESET_USER_VIRTUAL_BACKGROUND });

  const {
    cameraPreviewElement,
    clearVirtualBackground,
    initializeBackgroundFilter,
    getVideoInput,
    switchVideoInput,
    switchAudioInput
  } = vendorConfig;

  const deviceActions = {
    addToAttendeeTiles,
    handleVideoInputChanged: setVideoInputDevice,
    handleAudioInputChanged: setGenericAudioInputDevice,
    showNotification
  };

  return (
    <StyledModal
      isOpen={isOpen}
      fade={!isMobile}
      style={style}
      data-testId="speaker-settings"
    >
      <GenericCameraSourceSettings
        title={presenterTitle}
        description={presenterDescription}
        onSubmit={onSubmit}
        onCancel={onCancel}
        audioInputDeviceId={audioInputDevice}
        audioOutputDeviceId={audioOutputDevice}
        videoInputDeviceId={videoInputDevice?.device ?? videoInputDevice}
        isCameraAvailable={isCameraAvailable}
        userBackground={userBackground}
        transformedVideoInputs={transformedVideoInputs}
        uploadUserVirtualBackground={handleUploadUserVirtualBackground}
        deleteUserVirtualBackground={handleDeleteUserVirtualBackground}
        resetUserVirtualBackground={handleResetUserVirtualBackground}
        clearPreviewVirtualBackground={clearPreviewVirtualBackground}
        user={user}
        CameraPreviewElement={cameraPreviewElement}
        clearVirtualBackground={clearVirtualBackground}
        initializeBackgroundFilter={initializeBackgroundFilter}
        getVideoInput={getVideoInput}
        switchVideoInput={switchVideoInput}
        switchAudioInput={switchAudioInput}
        currentVideoTrack={currentVideoTrack}
        client={client}
        dispatch={dispatch}
        deviceActions={deviceActions}
      />
    </StyledModal>
  );
};

export default SettingsModal;
